import React, { useMemo, useRef, useState } from 'react';
import { useCompareKeyMetricsChart } from '../../hooks';
import {
  dealWithMentionsSentimentChart,
  getGameColorOrName,
} from '../../utils';
import { echartsContent, sortMapAuxiliaryFunc } from '@/pages/opinion/utils';
import { formatTimeXAxisText } from '@op/utils/chart';
import useLanguage from '@op/hooks/useLanguage';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import TableCommonHook from '@op/components/TableCommonHook';
import { SortBy } from 'tea-component/lib/table/addons';
import './index.scss';
import {
  GameStoreOneOptionsType,
  gameStoreTwoOptions,
  GameStoreTwoOptionsType,
  TableHeight,
} from '../../constants';
import { reviewsFormat, scoreFormat } from '@op/utils/value';
import { formatDateFunc } from '@op/utils/time';
import TableColumn from '../../component/TableColumn';
import CardCommon from '@op/components/CardCommon';
import { getGameDataIndex } from '@op/components/TableCommonHook/hooks/useCompareKeyMetricsColumns';
import ProgressColumn from '../../component/ProgressColumn';

export default function MobileStoreCharts({
  filter,
  store,
  type,
  chartType = 'line',
  gameData,
  subType,
  options: gameStoreOptions,
}: {
  filter: any;
  store: GameStoreOneOptionsType;
  type: GameStoreTwoOptionsType;
  chartType?: string;
  gameData: {
    [id: string]: {
      name: string;
      color: string;
    };
  }[];
  subType: string;
  options: {
    text: string;
    value: string;
    format?: (value: number) => number | string;
  }[];
}) {
  const { formatMessage } = useLanguage();
  const [sortBy, setSortBy] = useState<SortBy<any>>({
    by: 'time',
    order: 'desc',
  });

  const getGameData = getGameColorOrName(gameData);

  const { tooltip, formatData, dataType } = useMemo(() => {
    const item = gameStoreOptions.find((item) => item.value === subType);
    const { text, format, dataType } = item || {};
    const defaultFormat = (value: number) => reviewsFormat(value);
    return {
      tooltip: text ? formatMessage({ id: text }) : '',
      formatData: format ?? defaultFormat,
      dataType: dataType ?? '',
    };
  }, [formatMessage, gameStoreOptions, subType]);

  const params = useMemo(
    () => ({
      ...filter,
      stats_types: ['game_store'],
      tab_type: store,
      by_type: type,
      ...(type !== subType ? { sub_type: subType } : {}),
    }),
    [type, filter, store, subType],
  );

  const { data: statistics, loading } = useCompareKeyMetricsChart({
    filter: params,
    dealWithToChart: dealWithMentionsSentimentChart,
  });

  const xAxisIntervalLimit = 8;
  const chartRef = useRef(null);
  const { xAxisData, yAxisData, tableData } = statistics;
  const hasStatistics = Array.isArray(xAxisData) && xAxisData.length > 0;

  const columns = useMemo(
    () => [
      {
        header: formatMessage({ id: 'Date' }),
        key: 'time',
        align: 'left',
        render: (cvm: any) => formatDateFunc(params?.date_type)(cvm.time),
      },
      {
        header: formatMessage({ id: 'Game' }),
        key: 'name',
        align: 'left',
        render: (cvm: any) => {
          const item: any = getGameData(cvm?.name, 'all');
          return <TableColumn name={item?.entity_name} item={item} />;
        },
      },
      {
        header: tooltip,
        key: 'value',
        align: 'center',
        render: (cvm: any) => (
          <ProgressColumn
            percentage={cvm.ratio}
            value={cvm.value}
            formateValue={formatData}
            oldTime={[]}
            newTime={[]}
            oldValue={''}
          ></ProgressColumn>
        ),
      },
    ],
    [formatData, formatMessage, getGameData, params?.date_type, tooltip],
  );

  const options = useMemo(() => {
    if (hasStatistics) {
      let legendData: {
        name: string;
        itemStyle: {
          color: string;
        };
      }[] = [];
      let seriesData: {
        itemStyle?: any;
        barMaxWidth?: string;
        barGap?: string;
        label?: any;
        name: string;
        type: string;
        stack?: string;
        data: (number | null)[];
      }[] = [];
      yAxisData.map((item: { name: string; data: number[] }) => {
        const color = getGameData(item.name, 'color');
        seriesData.push({
          name: item.name,
          type: chartType,
          stack: chartType === 'bar' ? 'total' : '',
          data: item.data.map((item: number) => {
            if (type === 'lifetime' && item === 0) return null;
            return item;
          }),
          barMaxWidth: '70px',
          itemStyle: { color },
        });

        legendData.push({
          name: item.name,
          itemStyle: { color },
        });
      });
      const sortMap = getGameDataIndex(gameData);
      legendData.sort(sortMapAuxiliaryFunc(sortMap, (item) => item.name));
      const option = {
        tooltip: {
          trigger: 'axis',
          borderWidth: 0,
          padding: 0,
          confine: true,
          extraCssText: 'z-index:8',
          formatter: (data: any[]) => {
            const sumKey = data[0].axisValue;
            let timeText = '';
            if (params?.date_type) {
              timeText = formatDateFunc(params?.date_type)(sumKey);
            }
            return formatChartParamsDate({
              params: data,
              title: timeText,
              rightTitle: tooltip,
              listTitleFunc: (item) => getGameData(item.seriesName),
              valueFormat: formatData,
              isSort: true,
            });
          },
        },
        legend: {
          data: legendData,
          formatter: (params: string) => getGameData(params),
        },
        axisPointer: getAxisPointer((data) =>
          formatTimeXAxisText(data.value, params?.date_type),
        ),
        xAxis: {
          type: 'category',
          boundaryGap: ['20%', 0],
          data: xAxisData,
          axisLabel: {
            interval: xAxisData?.length > xAxisIntervalLimit ? 'auto' : 0,
            fontSize: 10,
            showMaxLabel: true,
            formatter: (value: any) =>
              formatTimeXAxisText(value, params?.date_type),
          },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => echartsContent(value),
            fontSize: 11,
          },
        },
        series: seriesData,
      };

      if (dataType === 'percent') {
        option.yAxis = {
          type: 'value',
          splitNumber: 5,
          max: 1,
          min: 0,
          axisLabel: {
            formatter: (value: any) => `${(value * 100).toFixed(0)}%`,
            fontSize: 11,
          },
        };
      }

      return option;
    }
    return {};
  }, [
    hasStatistics,
    yAxisData,
    gameData,
    xAxisData,
    dataType,
    getGameData,
    chartType,
    type,
    params?.date_type,
    tooltip,
    formatData,
  ]);

  return (
    <div ref={chartRef} className="dbo-mobile-store-chart">
      <CardCommon
        contentCard
        loading={loading}
        loadingHeight={TableHeight.CHART_LOADING}
        empty={!hasStatistics}
        emptyHeight={TableHeight.CHART_LOADING}
      >
        {chartType !== 'table' ? (
          <EChartCommon
            options={options}
            leftTitle={tooltip}
            height={TableHeight.CHART}
            optionsProps={{ type: chartType }}
          />
        ) : (
          <TableCommonHook
            recordKey={'comment_uin'}
            records={tableData || []}
            total={tableData?.length || 0}
            columns={columns}
            sortColumns={['time', 'value']}
            hasPageable={false}
            defaultSort={sortBy}
            minWidth="auto"
            sortInfo={[sortBy, setSortBy]}
            sortType={'local'}
            bordered="all"
            maxHeight={TableHeight.CHART_TABLE}
          />
        )}
      </CardCommon>
    </div>
  );
}
